import React, { useState } from "react"
import {
  AdderTypes, 
  dollarsToNum, 
  dollars, 
  IEquipmentOption, 
  EquipmentType, 
  LoanCalculations,
  Adders,
  validateSolarCost,
  validateRoofCost,
  validateStoragePrice,
  validatePowerwallQty,
  validateStorageQty,
  validateHvacCost,
  batteryPricePerKwhValidationMessage,
  storageSolarPctCapMessage,
  getCostCapsForValidation,
  type ProjectCostCaps,
  type TenantCostCapSource
} from "@oneethos/shared"
import { useAppState, usePpw } from "../hooks"
import { getSystemLabels } from "../lib/teslaSystemDetailsLabels"
import { NumericInput, DollarsInput, TextInput } from "./forms"
import './system-detail-inputs.scss'
import { FaTrash } from "react-icons/fa"
import { FastTrackFields } from './'
import TotalCostsSummary from './total-costs-summary'

type SystemDetailInputsProps = {
  formData: any
  handleChange: (formData: any) => void
  selectedEquipment: Partial<IEquipmentOption> | null
  isResidential: boolean
  disabled?: boolean
  actionButtons?: React.ComponentType<{ isValid: boolean }>
  showValidation?: boolean
  projectCostCaps?: ProjectCostCaps | null
  costCapsFallback?: 'tenant' | 'none'
}

export const SystemDetailInputs = ({
  formData,
  handleChange,
  disabled,
  selectedEquipment,
  isResidential,
  showValidation,
  actionButtons,
  projectCostCaps,
  costCapsFallback = 'tenant',
}: SystemDetailInputsProps) => {
  const { ppwPV, ppwStorage } = usePpw(formData)
  const [hasTouchedSystemSize, setHasTouchedSystemSize] = useState(ppwPV !== undefined)
  const { tenant: { config } } = useAppState()
  const cfg = (config ?? {}) as TenantCostCapSource
  const tenantCaps: TenantCostCapSource = {
    roofCostCap: cfg.roofCostCap,
    storageCostCap: cfg.storageCostCap,
    batteryCostCap: cfg.batteryCostCap,
    hvacCostCap: cfg.hvacCostCap,
  }
  const { roofCostCap, storageCostCap, batteryCostCap, hvacCostCap } = getCostCapsForValidation(
    projectCostCaps,
    tenantCaps,
    costCapsFallback,
  )

  const systemLabels = getSystemLabels(selectedEquipment)
  const adders = new Adders(formData.adders)
  const customAdders = adders.getCustomAdders()
  const downpaymentAmount = adders.costFor(AdderTypes.Downpayment)
  const storagePriceAmount = adders.costFor(AdderTypes.StoragePrice)

  // Derived values
  const PPW_THRESHOLD = 3.75
  const minimumAmount = isResidential ? 10000 : 25000
  const isStorageOnly = selectedEquipment?.id === EquipmentType.StorageOnly
  const isTeslaEquipment = selectedEquipment?.isTesla
  const systemCost = LoanCalculations.calcSystemCost({ ...formData, adders: adders.getList() })
  const loanAmount = LoanCalculations.calcLoanAmount({ ...formData, adders: adders.getList() })
  const solarCostNum = dollarsToNum(formData.solarCost)
  const capacityKwh = parseFloat(String(formData.storageCapacity)) || 0

  const showPpwWarning = !selectedEquipment?.isTesla
    && hasTouchedSystemSize
    && ppwPV > PPW_THRESHOLD

  // Adder values from formData
  const storagePrice = formData.adders?.find(a => a.description === AdderTypes.StoragePrice)?.amount
  const roofPrice = formData.adders?.find(a => a.description === AdderTypes.RoofPrice)?.amount
  const hvacPrice = formData.adders?.find(a => a.description === AdderTypes.HvacPrice)?.amount
  const otherPrice = formData.adders?.find(a => a.description === AdderTypes.Other)?.amount

  // Validation logic consolidated from PaymentCalculator
  const loanAmountIsValid = loanAmount >= minimumAmount
  const solarCostIsValid = solarCostNum >= minimumAmount
  const systemSizeIsValid = dollarsToNum(formData.systemSizeKW) > 0
  const storagePriceIsValid = storagePriceAmount >= minimumAmount
  const teslaPowerwallQtyIsValid = dollarsToNum(formData.teslaPowerwallQty) > 0
  const storageCapacityIsValid = dollarsToNum(formData.storageCapacity) > 0
  const storageQtyIsValid = dollarsToNum(formData.storageQty) > 0
  const downpaymentIsValid = downpaymentAmount <= systemCost
  const hasStorageBeingUsed = storagePriceAmount > 0 || teslaPowerwallQtyIsValid

  // Commercial/Non-profit specific validation
  const commercialValidationIsValid = isResidential || (
    dollarsToNum(formData.avgMonthlyUtilityBill) > 0 &&
    dollarsToNum(formData.estAvgMonthlyUtilityBillAfterSolar) >= 0 &&
    dollarsToNum(formData.estAvgMonthlyUtilityBillAfterSolar) < dollarsToNum(formData.avgMonthlyUtilityBill)
  )

  const isRoofInputValid = (
    validateRoofCost(roofPrice ?? '', systemCost, roofCostCap, systemLabels.hasRoofInput) === ''
  )

  const isHvacInputValid = (
    validateHvacCost(hvacPrice ?? '', systemCost, hvacCostCap, systemLabels.hasHvacInput) === ''
  )

  const isStorageInputValid = (
    validateStoragePrice(storagePrice ?? '', {
      storageQty: formData.storageQty,
      teslaPowerwallQty: formData.teslaPowerwallQty,
      solarCost: formData.solarCost,
      isStorageOnly,
      isTeslaEquipment,
      storageCostCap,
    }) === ''
  )

  const batteryPricePerKwhErrorMessage = batteryPricePerKwhValidationMessage(
    storagePriceAmount,
    capacityKwh,
    batteryCostCap,
    isTeslaEquipment
  )
  
  const isBatteryInputValid = batteryPricePerKwhErrorMessage === ''
  const costCapsAreValid = isRoofInputValid && isStorageInputValid && isBatteryInputValid && isHvacInputValid
     
  const getEquipmentValidation = (): boolean => {

    const commonValidations = downpaymentIsValid && loanAmountIsValid && costCapsAreValid

    switch (selectedEquipment?.id) {
      case EquipmentType.StorageOnly:
        return commonValidations &&
          storagePriceIsValid &&
          storageCapacityIsValid &&
          storageQtyIsValid

      case EquipmentType.TeslaRoof:
      case EquipmentType.TeslaSolar:
        return commonValidations &&
          solarCostIsValid &&
          systemSizeIsValid &&
          (!hasStorageBeingUsed || (storagePriceAmount > 0 && storageCapacityIsValid && teslaPowerwallQtyIsValid))

      case EquipmentType.StandardSolar:
      default:
        return commonValidations &&
          solarCostIsValid &&
          systemSizeIsValid &&
          commercialValidationIsValid &&
          (!hasStorageBeingUsed || storageCapacityIsValid)
    }
  }

  const displayCalculatePaymentBtn = getEquipmentValidation()

  const validationMessages = [
    !isStorageOnly && { isValid: solarCostIsValid, message: 'Please provide a valid Solar Price.' },
    !isStorageOnly && { isValid: systemSizeIsValid, message: 'Please provide a valid System Size.' },
    { isValid: loanAmountIsValid, message: `Loan amount must be at least ${dollars(minimumAmount)}.` },
    { isValid: !hasStorageBeingUsed || storageCapacityIsValid, message: 'Please provide a valid Storage Capacity.' },
    { isValid: commercialValidationIsValid, message: 'Please provide valid utility bill information for commercial/non-profit projects.' },
  ].filter(Boolean).filter(msg => !msg.isValid)

  return <div>
    <div className="system-details">
      <div className="system-prices">
        {systemLabels.hasSolarInput ?
          <div className="form-group">
            <label className="form-label">{systemLabels.solarLabel}*</label>
            <DollarsInput
              placeholder="$10,000"
              disabled={disabled}
              value={formData.solarCost}
              extraValidator={v => validateSolarCost(v, minimumAmount)}
              onChange={v => handleChange({
                ...formData,
                solarCost: v
              })}
            />
          </div> : null
        }
        {systemLabels.hasRoofInput ?
          <div className="form-group">
            <label className="form-label">{AdderTypes.RoofPrice}</label>
            <DollarsInput
              placeholder="$0.00"
              emptyOk
              disabled={disabled}
              before="+"
              value={roofPrice}
              onChange={v => handleChange({
                ...formData,
                adders: adders.updateAdder(formData.adders, AdderTypes.RoofPrice, v)
              })}
              extraValidator={v => validateRoofCost(v, systemCost, roofCostCap, systemLabels.hasRoofInput)}
            />
          </div> : null
        }
        <div className='adders-container'>
          <div className="form-group">
            <label className="form-label">{systemLabels.storageLabel}{isStorageOnly ? '*' : ''}</label>
            <DollarsInput
              placeholder="$0.00"
              emptyOk
              disabled={disabled}
              before="+"
              value={storagePrice}
              onChange={v => handleChange({
                ...formData,
                adders: adders.updateAdder(formData.adders, AdderTypes.StoragePrice, v)
              })}
              extraValidator={v => validateStoragePrice(v, {
                storageQty: formData.storageQty,
                teslaPowerwallQty: formData.teslaPowerwallQty,
                solarCost: formData.solarCost,
                isStorageOnly: isStorageOnly,
                isTeslaEquipment: isTeslaEquipment,
                storageCostCap: storageCostCap,
              })}
            />
          </div>
          {systemLabels.hasPowerwallQtyInput ?
            <div className="form-group">
              <label className="form-label">Powerwall Quantity</label>
              <NumericInput
                emptyOk
                placeholder="0"
                disabled={disabled}
                value={formData.teslaPowerwallQty}
                onChange={v => {
                  handleChange({
                    ...formData,
                    teslaPowerwallQty: v
                  })
                }}
                extraValidator={v => validatePowerwallQty(v, dollarsToNum(storagePrice))}
              />
            </div> : null
          }
          {isStorageOnly || (systemLabels.hasStorageQtyInput && dollarsToNum(storagePrice) > 0) ?
            <div className="form-group">
              <label className="form-label">{isStorageOnly ? 'Battery Quantity*' : 'Storage Quantity*'}</label>
              <NumericInput
                emptyOk
                placeholder="0"
                disabled={disabled}
                value={formData.storageQty}
                onChange={v => {
                  handleChange({
                    ...formData,
                    storageQty: v
                  })
                }}
                extraValidator={v => validateStorageQty(v, dollarsToNum(storagePrice), isStorageOnly)}
              />
            </div> : null
          }
          {systemLabels.hasHvacInput ?
            <div className="form-group">
              <label className="form-label">{AdderTypes.HvacPrice}</label>
              <DollarsInput
                placeholder="$0.00"
                emptyOk
                disabled={disabled}
                value={hvacPrice}
                before="+"
                onChange={v => handleChange({
                  ...formData,
                  adders: adders.updateAdder(formData.adders, AdderTypes.HvacPrice, v)
                })}
                extraValidator={v => validateHvacCost(v, systemCost, hvacCostCap, systemLabels.hasHvacInput)}
              />
            </div> : null
          }
          {customAdders.map(customAdder => (
            <div key={customAdder.description} className="form-group">
              <label className="form-label">{customAdder.description}</label>
              <DollarsInput
                placeholder="$0.00"
                emptyOk
                disabled={disabled}
                value={formData.adders?.find(a => a.description === customAdder.description)?.amount}
                before="+"
                after={
                  <button
                    type="button"
                    className="btn p-0 border-0 bg-transparent"
                    onClick={() => handleChange({
                      ...formData,
                      adders: formData.adders?.filter(a =>
                        a.description !== customAdder.description) || []
                    })}
                    disabled={disabled}
                    style={{
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      lineHeight: '1',
                      display: 'flex',
                      width: 'auto',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                    aria-label={`Remove ${customAdder.description}`}
                  >
                    <FaTrash />
                  </button>
                }
                onChange={v => handleChange({
                  ...formData,
                  adders: [
                    ...(formData.adders?.filter(a =>
                      a.description !== customAdder.description) || []),
                    { description: customAdder.description, amount: v }
                  ]
                })}
              />
            </div>
          ))}
          <div className="form-group">
            <label className="form-label">{AdderTypes.Other} <sup>†</sup></label>
            <DollarsInput
              placeholder="$0.00"
              emptyOk
              disabled={disabled}
              value={otherPrice}
              before="+"
              onChange={v => handleChange({
                ...formData,
                adders: adders.updateAdder(formData.adders, AdderTypes.Other, v)
              })}
            />
          </div>
        </div>
        <TotalCostsSummary
          systemCost={dollars(systemCost)}
          loanAmount={dollars(loanAmount)}
          formData={formData}
          handleChange={handleChange}
          disabled={disabled}
        />
        {!isResidential ? <FastTrackFields
          formData={formData}
          handleChange={handleChange}
          disabled={disabled}
        /> : null}
      </div>
      <div className="system-size">
        {systemLabels.hasSystemSizeInput ? (
          <div className="form-group">
            <label className="form-label">{systemLabels.systemSizeKW}*</label>
            <NumericInput
              disabled={disabled}
              value={formData.systemSizeKW}
              onBlur={() => setHasTouchedSystemSize(true)}
              onChange={v => handleChange({
                ...formData,
                systemSizeKW: v
              })}
              extraValidator={v => dollarsToNum(v) === 0 ? 'System Size is required' : ''}
            />
          </div>
        ) : null}
        {!isStorageOnly &&
          <div className="form-group">
            <label className="form-label">{systemLabels.pricePerWatt}</label>
            <TextInput
              disabled={true}
              value={systemLabels.pricePerWatt === 'Price Per Watt Hour'
                ? (ppwStorage ? dollars(ppwStorage) : '')
                : ppwPV ? dollars(ppwPV) : ''
              }
            />
          </div>}
        {(systemLabels.hasStorageCapacityInput && dollarsToNum(storagePrice) > 0) ? (
          <div className="form-group">
            <label className="form-label">Storage Capacity (kWh)*</label>
            <NumericInput
              disabled={disabled}
              value={formData.storageCapacity}
              onChange={v => handleChange({
                ...formData,
                storageCapacity: v
              })}
            />
          </div>) : null}
        {systemLabels.otherContent}
        <div className="mobile-totals">
          <TotalCostsSummary
            systemCost={dollars(systemCost)}
            loanAmount={dollars(loanAmount)}
            formData={formData}
            handleChange={handleChange}
            disabled={disabled}
          />
        </div>
      </div>
    </div>
    {showPpwWarning && (
      <div className="alert alert-danger">
        Loan applications with PPW higher than ${PPW_THRESHOLD} are unlikely to be approved.
      </div>
    )}

    {!isBatteryInputValid && (
      <div className="alert alert-danger">
        {batteryPricePerKwhErrorMessage}
      </div>
    )}

    {/* Validation messages */}
    {showValidation && document.activeElement.tagName !== 'INPUT' && validationMessages.length > 0 && (
      <div className="alert alert-info">
        {validationMessages.map((error, index) => (
          <div key={index}>{error.message}</div>
        ))}
      </div>
    )}

    {/* Action buttons */}
    {actionButtons && React.createElement(actionButtons, { isValid: displayCalculatePaymentBtn })}
  </div>
}

export default SystemDetailInputs